import { useState, useEffect, ReactNode } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Slider } from "@/components/ui/slider";
import { Loader2, LucideIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useTranslation } from "react-i18next";
import { usePopupMessageContext } from "@/components/PopupMessageProvider";
import { format } from "date-fns";
import { useDateLocale } from "@/hooks/useDateLocale";
import { cn } from "@/lib/utils";
import { getCurrentUser, getProgressLogDates, getProgressLogByDate } from "@/lib/api";
import { ApiResult, ProgressLog } from "@/lib/api";

// Slider configuration for slider input type
export interface SliderConfig {
  min: number;
  max: number;
  step: number;
  defaultValue: number;
  showScale?: boolean;
  scaleLabels?: string[]; // Labels to show below slider (e.g., emojis)
}

// Props interface for BaseMetricLogCard - accepts pre-translated strings
export interface BaseMetricLogCardProps {
  // Display
  icon: LucideIcon;
  gradientClasses: string;
  borderHoverClasses: string;

  // Input type
  inputType: 'text' | 'slider';
  sliderConfig?: SliderConfig;

  // Data field (for progress_logs table)
  fieldName?: 'cigarettes_smoked' | 'mood_rating' | 'stress_level' | 'anxiety_level';

  // Validation (for text input)
  minValue: number;
  maxValue?: number;

  // Pre-translated strings (Pattern 1)
  cardTitle: string;
  cardDescription: string;
  dialogTitle: string;
  dialogDescription: string;
  inputLabel: string;
  inputPlaceholder: string;
  scaleDescription?: string;
  notesPlaceholder: string;
  loggedTitle: string;
  loggedDescription: string;
  errorInvalidInput: string;
  errorInvalidValue: string;

  // Analytics
  analyticsEvent: string;
  analyticsDataMapper?: (value: number, date: string) => Record<string, any>;

  // Popup triggers
  popupTriggers?: Array<{
    trigger: string;
    condition?: (value: number) => boolean;
    contextMapper?: (value: number) => Record<string, any>;
  }>;

  // Custom value display (e.g., emoji for mood)
  valueDisplay?: (value: number) => ReactNode;

  // Custom data source functions (for non-progress_logs tables like cravings)
  customFetchDates?: (userId: string) => Promise<ApiResult<string[]>>;
  customFetchValue?: (userId: string, date: Date) => Promise<number | null>;
  customUpdateFunction?: (userId: string, date: Date, value: number, notes?: string, productId?: string) => Promise<ApiResult<any>>;

  // Standard update function (for progress_logs table)
  updateFunction?: (
    userId: string,
    date: string,
    value: number,
    notes?: string,
    productId?: string
  ) => Promise<ApiResult<ProgressLog>>;

  // Callbacks
  onComplete?: () => void;
  onDataUpdate?: () => void;
  isLoading?: boolean;

  // External control for opening dialog
  externalOpen?: boolean;
  onExternalOpenChange?: (open: boolean) => void;

  // Product context for multi-product isolation
  productId?: string;
}

export function BaseMetricLogCard({
  icon: Icon,
  gradientClasses,
  borderHoverClasses,
  inputType,
  sliderConfig,
  fieldName,
  minValue,
  maxValue,
  cardTitle,
  cardDescription,
  dialogTitle,
  dialogDescription,
  inputLabel,
  inputPlaceholder,
  scaleDescription,
  notesPlaceholder,
  loggedTitle,
  loggedDescription,
  errorInvalidInput,
  errorInvalidValue,
  analyticsEvent,
  analyticsDataMapper,
  popupTriggers,
  valueDisplay,
  customFetchDates,
  customFetchValue,
  customUpdateFunction,
  updateFunction,
  onComplete,
  onDataUpdate,
  isLoading,
  externalOpen,
  onExternalOpenChange,
  productId
}: BaseMetricLogCardProps) {
  const defaultValue = inputType === 'slider' 
    ? sliderConfig?.defaultValue ?? minValue 
    : "";
  
  const [internalOpen, setInternalOpen] = useState(false);
  const [value, setValue] = useState<number | string>(defaultValue);
  const [notes, setNotes] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [recordedDates, setRecordedDates] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  // Support both internal and external open state
  const dialogOpen = externalOpen !== undefined ? externalOpen : internalOpen;
  const setDialogOpen = (open: boolean) => {
    if (onExternalOpenChange) {
      onExternalOpenChange(open);
    } else {
      setInternalOpen(open);
    }
  };

  const { toast } = useToast();
  const { trackFeatureUsage } = useAnalytics();
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const { checkTrigger } = usePopupMessageContext();

  useEffect(() => {
    if (dialogOpen) {
      fetchRecordedDates();
      fetchValueForDate(selectedDate);
    }
  }, [dialogOpen, selectedDate]);

  const fetchRecordedDates = async () => {
    try {
      const userResult = await getCurrentUser();
      if (!userResult.success) return;

      if (customFetchDates) {
        const result = await customFetchDates(userResult.data.id);
        if (result.success) {
          setRecordedDates(new Set(result.data));
        }
      } else if (fieldName) {
        const result = await getProgressLogDates(userResult.data.id, fieldName);
        if (result.success) {
          setRecordedDates(new Set(result.data));
        }
      }
    } catch (error) {
      console.error('Error fetching recorded dates:', error);
    }
  };

  const fetchValueForDate = async (date: Date) => {
    try {
      const userResult = await getCurrentUser();
      if (!userResult.success) return;

      if (customFetchValue) {
        const fetchedValue = await customFetchValue(userResult.data.id, date);
        if (fetchedValue !== null) {
          setValue(fetchedValue);
        } else {
          setValue(inputType === 'slider' ? sliderConfig?.defaultValue ?? minValue : "");
        }
      } else if (fieldName) {
        const dateString = format(date, 'yyyy-MM-dd');
        const result = await getProgressLogByDate(userResult.data.id, dateString);

        if (result.success && result.data && result.data[fieldName] !== null) {
          setValue(result.data[fieldName]!);
        } else {
          setValue(inputType === 'slider' ? sliderConfig?.defaultValue ?? minValue : "");
        }
      }
    } catch (error) {
      console.error(`Error fetching value:`, error);
    }
  };

  const handleSubmit = async () => {
    const numValue = typeof value === 'number' ? value : parseFloat(value);

    // Validation
    if (inputType === 'text' && (value === "" || isNaN(numValue) || numValue < minValue)) {
      toast({
        title: errorInvalidInput,
        description: errorInvalidValue,
        variant: "destructive",
      });
      return;
    }

    if (maxValue !== undefined && numValue > maxValue) {
      toast({
        title: errorInvalidInput,
        description: errorInvalidValue,
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const userResult = await getCurrentUser();
      if (!userResult.success) return;

      const dateString = format(selectedDate, 'yyyy-MM-dd');
      
      // Use custom update function or standard one
      if (customUpdateFunction) {
        const result = await customUpdateFunction(
          userResult.data.id,
          selectedDate,
          Math.round(numValue),
          notes || undefined,
          productId
        );
        if (!result.success) {
          const errorMessage = 'error' in result ? result.error.message : t('common.basemetriclogcard.failed_log', 'Failed to log');
          throw new Error(errorMessage);
        }
      } else if (updateFunction) {
        const result = await updateFunction(
          userResult.data.id,
          dateString,
          Math.round(numValue),
          notes || undefined,
          productId
        );
        if (!result.success) {
          const errorMessage = 'error' in result ? result.error.message : `Failed to log ${fieldName}`;
          throw new Error(errorMessage);
        }
      }

      // Analytics
      const analyticsData = analyticsDataMapper 
        ? analyticsDataMapper(numValue, dateString)
        : { value: numValue, date: dateString };
      trackFeatureUsage(analyticsEvent, analyticsData);

      // Toast
      toast({
        title: loggedTitle,
        description: loggedDescription,
      });

      // Popup triggers
      if (popupTriggers) {
        for (const trigger of popupTriggers) {
          if (!trigger.condition || trigger.condition(numValue)) {
            const context = trigger.contextMapper ? trigger.contextMapper(numValue) : undefined;
            checkTrigger(trigger.trigger, context);
          }
        }
      }

      await fetchRecordedDates();
      setValue(inputType === 'slider' ? sliderConfig?.defaultValue ?? minValue : "");
      setNotes("");
      setDialogOpen(false);
      onDataUpdate?.();
      onComplete?.();
    } catch (error: any) {
      toast({
        title: t('common:errors.generic', 'Error'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const renderInput = () => {
    if (inputType === 'slider' && sliderConfig) {
      const { min, max, step, scaleLabels } = sliderConfig;
      const numValue = typeof value === 'number' ? value : min;
      
      return (
        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center justify-between">
            <span>
              {inputLabel} {format(selectedDate, 'MMM d, yyyy', { locale: dateLocale })}
            </span>
            {valueDisplay && (
              <span className="text-2xl">{valueDisplay(numValue)}</span>
            )}
            {!valueDisplay && (
              <span className="text-sm font-semibold">{numValue}/{max}</span>
            )}
          </label>
          <Slider
            value={[numValue]}
            onValueChange={(v) => setValue(v[0])}
            min={min}
            max={max}
            step={step}
            className="w-full"
          />
          {scaleLabels && scaleLabels.length === 10 && (
            <div className="flex justify-between text-center mt-3">
              {scaleLabels.map((emoji, i) => {
                const position = i + 1;
                const isSelected = numValue === position;
                const isBeforeSelected = position <= numValue;
                
                // Color gradient from green (calm) to red (stressed)
                const getGradientColor = (pos: number) => {
                  if (pos <= 3) return "from-emerald-500 to-green-400";
                  if (pos <= 5) return "from-yellow-400 to-amber-400";
                  if (pos <= 7) return "from-orange-400 to-orange-500";
                  return "from-red-500 to-rose-500";
                };
                
                return (
                  <div 
                    key={i} 
                    className={cn(
                      "flex flex-col items-center transition-all duration-300",
                      isSelected ? "scale-125" : "scale-100",
                      !isBeforeSelected && "opacity-40"
                    )}
                  >
                    <div className={cn(
                      "rounded-full p-1 transition-all duration-300",
                      isSelected && `bg-gradient-to-br ${getGradientColor(position)} shadow-lg`,
                      isBeforeSelected && !isSelected && "bg-muted/50"
                    )}>
                      <span className={cn(
                        "text-lg transition-all duration-300",
                        isSelected && "drop-shadow-sm"
                      )}>{emoji}</span>
                    </div>
                    <span className={cn(
                      "text-xs transition-all duration-300",
                      isSelected ? "font-semibold text-foreground" : "text-muted-foreground"
                    )}>{position}</span>
                  </div>
                );
              })}
            </div>
          )}
          {scaleLabels && scaleLabels.length === 3 && (
            <div className="flex justify-between text-sm text-muted-foreground">
              {scaleLabels.map((label, i) => {
                const isEmoji = /\p{Emoji}/u.test(label);
                return (
                  <span key={i} className={isEmoji ? "text-2xl" : ""}>
                    {label}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Text input (original behavior)
    return (
      <div className="space-y-2">
        <Label htmlFor="metric-value">
          {inputLabel} {format(selectedDate, 'MMM d, yyyy', { locale: dateLocale })}
        </Label>
        <Input
          id="metric-value"
          type="number"
          min={minValue}
          max={maxValue}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={inputPlaceholder}
        />
        {scaleDescription && (
          <p className="text-xs text-muted-foreground">
            {scaleDescription}
          </p>
        )}
      </div>
    );
  };

  return (
    <>
      <Card
        className={cn(
          "relative overflow-hidden cursor-pointer transition-all duration-300 group border-2 border-transparent hover:shadow-md",
          borderHoverClasses,
          isLoading && "pointer-events-none"
        )}
        onClick={() => setDialogOpen(true)}
      >
        <div className="absolute inset-0">
          <div className={cn("w-full h-full bg-gradient-to-br transition-all duration-500 group-hover:opacity-90", gradientClasses)} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10 transition-opacity duration-300 group-hover:from-black/70" />
        </div>

        {isLoading && (
          <div className="absolute inset-0 z-20 bg-black/40 backdrop-blur-sm flex items-center justify-center animate-fade-in">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          </div>
        )}

        <CardContent className="relative z-10 p-4 h-full flex flex-col justify-end min-h-[140px] sm:min-h-[160px]">
          <div className="mt-auto">
            <div className="mb-2 p-2 rounded-full bg-white/20 w-fit">
              <Icon className="w-5 h-5 text-white" />
            </div>
            <h4 className="font-bold text-white text-sm sm:text-lg leading-tight mb-1 transition-transform duration-300 group-hover:translate-x-1">
              {cardTitle}
            </h4>
            <p className="text-white/70 text-[10px] sm:text-xs line-clamp-2 transition-opacity duration-300 group-hover:text-white/90">
              {cardDescription}
            </p>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>
              {dialogDescription}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                disabled={(date) => date > new Date()}
                className="rounded-md border pointer-events-auto"
                modifiers={{
                  recorded: (date) => recordedDates.has(format(date, 'yyyy-MM-dd'))
                }}
                modifiersClassNames={{
                  recorded: "relative after:content-[''] after:absolute after:top-0.5 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:bg-primary"
                }}
              />
            </div>
            {renderInput()}
            <div className="space-y-2">
              <Label htmlFor="metric-notes">
                {t('common:common.notes', 'Notes')} ({t('common:common.optional', 'optional')})
              </Label>
              <Textarea
                id="metric-notes"
                placeholder={notesPlaceholder}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>
            <Button onClick={handleSubmit} disabled={saving} className="w-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {t('common:buttons.save', 'Save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
